import React from 'react';
import { TicketChat } from './TicketChat';
import { FiX, FiAlertTriangle } from 'react-icons/fi';
import { UserQuery } from './UserQueries.api';
import { isFinalStatus, TicketStatus, ticketReference } from './TicketDetails';
import { StatusMenu } from './StatusMenu';
import { canReply, category, dateLabel, initials } from './UserQueries.helpers';
import './UserQueriesThread.css';

type Props = {
  selected: UserQuery;
  sending: boolean;
  replyError: string;
  close: () => void;
  onUpdate: (ticket: UserQuery) => void;
  onBusy: (busy: boolean) => void;
  changeStatus: (status: string) => void;
};

/* Presentation of the admin ticket view. All state and handlers are owned by UserQueriesPage. */
export function QueryConversation({ selected, sending, replyError, close, onUpdate, onBusy, changeStatus }: Props) {
  const memberName = selected.user_name || 'Guest member';
  return <>
    <header className="uq-modal-header">
      <div className="min-w-0">
        <div className="zs-eyebrow">Member conversation</div>
        <h2 className="uq-modal-title">{canReply(selected) ? 'Reply to query' : 'Conversation details'}</h2>
      </div>
      <button className="zs-icon-btn shrink-0" aria-label="Close conversation" disabled={sending} onClick={close}><FiX /></button>
    </header>

    <div className="uq-detail">
      <aside className="uq-detail-side">
        <div className="uq-member">
          <span className="uq-avatar uq-avatar-lg">{initials(selected.user_name)}</span>
          <div className="uq-member-text">
            <strong>{memberName}</strong>
            <span className="uq-wrap">{selected.email || 'No email provided'}</span>
          </div>
        </div>
        <div className="mt-3"><TicketStatus query={selected} /></div>

        <div className="mt-5">
          <span className="block mb-1.5 zs-label">Ticket status</span>
          <StatusMenu value={selected.ticket_status} onChange={changeStatus}
            disabled={Number(selected.status_editable) === 0 || sending || selected.reply_state === 'sending'} />
        </div>
        {isFinalStatus(selected.ticket_status) && <p className="zs-hint">Resolved tickets are final. The status can’t be changed and the conversation is closed.</p>}
        {Number(selected.status_editable) === 0 && <p role="alert" className="uq-alert uq-alert-danger mt-3"><FiAlertTriangle aria-hidden="true" /><span>Ticket status setup is incomplete. Apply the queryTicketStatus.sql database migration, then refresh.</span></p>}

        <dl className="uq-meta">
          <div><dt>Reference</dt><dd className="font-mono">{ticketReference(selected)}</dd></div>
          <div><dt>Category</dt><dd>{category(selected)}</dd></div>
          <div><dt>Source</dt><dd className="capitalize">{selected.source}</dd></div>
          <div><dt>Received</dt><dd>{dateLabel(selected.created_at)}</dd></div>
        </dl>
      </aside>

      <div className="uq-detail-main">
        {replyError && <p role="alert" className="uq-alert uq-alert-danger mb-4"><FiAlertTriangle aria-hidden="true" /><span>{replyError}</span></p>}

        <TicketChat key={`${selected.source}-${selected.id}`} ticket={selected} admin disabled={sending} onUpdate={onUpdate} onBusy={onBusy} />
      </div>
    </div>

    {!canReply(selected) && <footer className="uq-modal-footer"><button className="zs-btn zs-btn-secondary" onClick={close}>Close conversation</button></footer>}
  </>;
}
